const TREE_TYPES = {
  TEAM: "TEAM",
  MANAGEMENT: "MANAGEMENT",
  TEAMS_ONLY: "TEAMS_ONLY",
  TEAMS_WITH_IDS: "TEAMS_WITH_IDS",
  WORKDAY: "WORKDAY",
};

export function adminOrgCsvMappings(args) {
  const { treeType, headers, currentMappings, csvRows, currentUserEmail } =
    args;

  return {
    treeType: treeType,
    headers: headers || [],
    extraLevels: [],
    csvRows: csvRows || [],
    activeTab: "csv", // 'csv' or 'json'
    transformedRows: [],
    currentUserEmail,

    init() {
      if (currentMappings) {
        const extraLevels = Object.keys(currentMappings)
          .filter((key) => key.match(/^extra_level_\d+$/))
          .map((key) => parseInt(key.replace("extra_level_", "")))
          .sort((a, b) => a - b);

        this.extraLevels = extraLevels;
      }

      // Initial transformation after a short delay to ensure DOM is ready
      this.$nextTick(() => {
        this.updateTransformation();
      });
    },

    getCurrentMapping(key) {
      return currentMappings?.[key] || "";
    },

    addLevel() {
      const newLevel = this.extraLevels.length + 1;
      this.extraLevels.push(newLevel);
    },

    removeLevel(levelToRemove) {
      this.extraLevels = this.extraLevels.filter(
        (level) => level !== levelToRemove,
      );

      // Renumber remaining levels to maintain sequential order
      this.extraLevels = this.extraLevels.map((_, index) => index + 1);
    },

    shouldShowReferenceIds() {
      return (
        this.treeType === TREE_TYPES.TEAMS_WITH_IDS ||
        this.treeType === TREE_TYPES.WORKDAY
      );
    },

    truncateText(text, maxLength) {
      if (text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength - 3) + "...";
    },

    switchTab(tab) {
      this.activeTab = tab;
    },

    getCurrentMappings() {
      const form = document.getElementById("mappings-form");
      if (!form) return {};

      const formData = new FormData(form);
      const mappings = {};

      for (let [key, value] of formData.entries()) {
        if (key.startsWith("mappings[") && key.endsWith("]")) {
          const mappingKey = key.slice(9, -1);
          if (value) {
            mappings[mappingKey] = value;
          }
        }
      }

      return mappings;
    },

    // Transform CSV data based on current mappings
    updateTransformation() {
      const mappings = this.getCurrentMappings();

      this.transformedRows = this.csvRows.slice(0, 20).map((rawRow) => {
        const row = this.convertRowToObject(rawRow);
        let transformed = {};

        // Basic fields
        if (mappings.email) {
          transformed.email = row[mappings.email] || "";
        }
        if (mappings.team_name) {
          transformed.team_name = row[mappings.team_name] || "";
        }
        if (mappings.mgr_email) {
          transformed.mgr_email = row[mappings.mgr_email] || "";
        }
        if (mappings.team_reference_id) {
          transformed.team_reference_id = row[mappings.team_reference_id] || "";
        }

        // Parent team fields
        if (mappings.parent_team) {
          transformed.parent_team = row[mappings.parent_team] || "";
        }
        if (mappings.parent_team_dri) {
          transformed.parent_team_dri = row[mappings.parent_team_dri] || "";
        }
        if (mappings.parent_team_reference_id) {
          transformed.parent_team_reference_id =
            row[mappings.parent_team_reference_id] || "";
        }

        // Grandparent team fields
        if (mappings.grandparent_team) {
          transformed.grandparent_team = row[mappings.grandparent_team] || "";
        }
        if (mappings.grandparent_team_dri) {
          transformed.grandparent_team_dri =
            row[mappings.grandparent_team_dri] || "";
        }
        if (mappings.grandparent_team_reference_id) {
          transformed.grandparent_team_reference_id =
            row[mappings.grandparent_team_reference_id] || "";
        }

        // Extra levels
        this.extraLevels.forEach((level) => {
          if (mappings[`extra_level_${level}`]) {
            transformed[`extra_level_${level}`] =
              row[mappings[`extra_level_${level}`]] || "";
          }
          if (mappings[`extra_level_${level}_dri`]) {
            transformed[`extra_level_${level}_dri`] =
              row[mappings[`extra_level_${level}_dri`]] || "";
          }
          if (mappings[`extra_level_${level}_reference_id`]) {
            transformed[`extra_level_${level}_reference_id`] =
              row[mappings[`extra_level_${level}_reference_id`]] || "";
          }
        });

        return transformed;
      });

      // Apply level compression
      this.transformedRows = this.transformedRows.map((row) =>
        this.compressLevels(row),
      );
    },

    // Compress levels by removing gaps in the hierarchy
    compressLevels(employee) {
      const compressed = { ...employee };

      // Collect all levels in order
      const allLevels = [
        {
          team_name: compressed.team_name || "",
          dri: compressed.mgr_email || "",
          reference_id: compressed.team_reference_id || "",
        },
        {
          team_name: compressed.parent_team || "",
          dri: compressed.parent_team_dri || "",
          reference_id: compressed.parent_team_reference_id || "",
        },
        {
          team_name: compressed.grandparent_team || "",
          dri: compressed.grandparent_team_dri || "",
          reference_id: compressed.grandparent_team_reference_id || "",
        },
      ];

      // Add extra levels in order
      this.extraLevels.forEach((level) => {
        allLevels.push({
          team_name: compressed[`extra_level_${level}`] || "",
          dri: compressed[`extra_level_${level}_dri`] || "",
          reference_id: compressed[`extra_level_${level}_reference_id`] || "",
        });
      });

      // Compress each field type independently
      const teamNames = allLevels
        .map((level) => level.team_name)
        .filter((name) => name);
      const dris = allLevels.map((level) => level.dri).filter((dri) => dri);
      const referenceIds = allLevels
        .map((level) => level.reference_id)
        .filter((id) => id);

      // Build result with compressed data
      const result = {
        email: compressed.email,
      };

      // Assign team names
      if (teamNames[0]) result.team_name = teamNames[0];
      if (teamNames[1]) result.parent_team = teamNames[1];
      if (teamNames[2]) result.grandparent_team = teamNames[2];
      for (let i = 3; i < teamNames.length; i++) {
        result[`extra_level_${i - 2}`] = teamNames[i];
      }

      // Assign DRIs
      if (dris[0]) result.mgr_email = dris[0];
      if (dris[1]) result.parent_team_dri = dris[1];
      if (dris[2]) result.grandparent_team_dri = dris[2];
      for (let i = 3; i < dris.length; i++) {
        result[`extra_level_${i - 2}_dri`] = dris[i];
      }

      // Assign reference IDs
      if (referenceIds[0]) result.team_reference_id = referenceIds[0];
      if (referenceIds[1]) result.parent_team_reference_id = referenceIds[1];
      if (referenceIds[2])
        result.grandparent_team_reference_id = referenceIds[2];
      for (let i = 3; i < referenceIds.length; i++) {
        result[`extra_level_${i - 2}_reference_id`] = referenceIds[i];
      }

      return result;
    },

    // Convert CSV row to proper object format and clean for display
    getRowAsJson(row) {
      // Convert from array format to object format
      const rowObject = this.convertRowToObject(row);

      // Filter out any null, undefined, or empty string values for cleaner display
      const cleanRow = {};
      Object.keys(rowObject).forEach((key) => {
        if (rowObject[key] != null && rowObject[key] !== "") {
          cleanRow[key] = rowObject[key];
        }
      });
      return cleanRow;
    },

    // Convert CSV row from array format ([header, value] pairs) to object format
    convertRowToObject(row) {
      const rowObject = {};

      // If row is already a proper object with string keys, return as-is
      if (row && typeof row === "object" && !Array.isArray(row)) {
        const keys = Object.keys(row);
        if (
          keys.length > 0 &&
          typeof keys[0] === "string" &&
          !keys[0].match(/^\d+$/)
        ) {
          return row;
        }
      }

      // Convert from array format (numeric keys with [header, value] arrays)
      Object.keys(row).forEach((key) => {
        if (row[key] && Array.isArray(row[key]) && row[key].length === 2) {
          const [header, value] = row[key];
          rowObject[header] = value;
        }
      });

      return rowObject;
    },
  };
}
